import React, { FC, useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form"; // הוספת useForm ו-Controller מ-`react-hook-form`
import SubTitle from "components/SubTitle";
import Alert from "react-bootstrap/Alert";
import { InfoCircleFill } from "react-bootstrap-icons";
import { FormikProps } from "formik";
import { Suggestion } from "pages/ControlCenter/ContentManagement/contentManagementUtils/tabSchema";
import { Col, Row, Form as FormControl } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { UserRole } from "components/Reports/reportAssets/assets";
import { useGetSuggestionListQuery } from "features/api/contentManagement/contentManagementApiSlice";
import TypeAheadReuseable from "components/bootstrap/TypeAheadReuseable";
import { suggestionTypeAheadProps } from "components/Reports/reportAssets/typeAheadProps";
import InputGroup from "react-bootstrap/InputGroup";
import Stack from "react-bootstrap/Stack";
import { FormControlsValues } from "./SummaryStepTypes";

type FormFeedbackSectionProps = {
  formikProps: FormikProps<FormControlsValues>;
  role: UserRole;
};

const FormFeedbackSection: FC<FormFeedbackSectionProps> = ({
  formikProps,
  role,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, errors, isSubmitting, handleChange } =
    formikProps;

  const { control } = useForm<FormControlsValues>();

  const { data: suggestions }: { data: Suggestion[] } =
    useGetSuggestionListQuery();
  const suggestionsOptions = suggestions
    ? suggestions.filter((suggestion) => !suggestion.disable)
    : [];

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<any>(null);

  const handleSuggestionChange = (e: any) => {
    if (e.length > 0) {
      setSelectedSuggestion({
        label: e[0]?.label,
        value: e[0]?.value,
      });
      const suggestion = suggestions?.find((s) => s.id === e[0]?.value);
      suggestion && setFieldValue("feedBackToVolunteer", suggestion?.text);
    } else {
      setSelectedSuggestion(null);
      setFieldValue("feedBackToVolunteer", "");
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;
    setFieldValue("feedBackToVolunteer", value);
  };

  useEffect(() => {
    if (values.reportSuggestionId && suggestions) {
      const suggestion = suggestions.find(
        (x) => x.id === values.reportSuggestionId
      );

      if (suggestion) {
        setSelectedSuggestion({
          label: suggestion?.title,
          value: suggestion?.id,
        });
        setFieldValue("feedBackToVolunteer", suggestion?.text);
      }
    }
  }, [
    values.reportSuggestionId,
    suggestions,
    setSelectedSuggestion,
    setFieldValue,
  ]);

  return (
    <div>
      <SubTitle className="default-vertical-padding">
        {t("reportVolunteerFeedback")}
      </SubTitle>

      <Stack className="mb-3">
        <InputGroup hasValidation>
          <FormControl.Label className="full-width-label">
            {t("selectSuggestion")}
            <Row className="violation-type-container default-vertical-padding">
              <Col sm={12}>
                {suggestionsOptions?.length ? (
                  <Controller
                    name="feedBackToVolunteer"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("validationRequired"),
                      },
                    }}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <TypeAheadReuseable
                          {...suggestionTypeAheadProps}
                          options={
                            suggestionsOptions?.map((item) => ({
                              label: item.title,
                              value: item.id,
                            })) || []
                          }
                          onChange={handleSuggestionChange}
                          selected={
                            selectedSuggestion
                              ? [
                                  {
                                    label: selectedSuggestion.label,
                                    value: selectedSuggestion.value,
                                  },
                                ]
                              : []
                          }
                        />
                        {errors.feedBackToVolunteer && (
                          <span className="requiredField">
                            *{t("validationRequired")}
                          </span>
                        )}
                      </>
                    )}
                  />
                ) : (
                  <div>Loading...</div>
                )}
              </Col>
            </Row>
          </FormControl.Label>

          <Alert variant="light">
            <p className="alert-description-text">
              <InfoCircleFill
                size={15}
                color="info"
                className="alert-description-icon"
              />
              {t("reportVolunteerFeedbackDescription")}
            </p>
          </Alert>

          <FormControl.Group as={Col} md="12">
            <div style={{ borderRadius: 10, padding: 5, border: "1px solid" }}>
              <textarea
                ref={textareaRef}
                value={values?.feedBackToVolunteer || ""}
                onChange={handleTextChange}
                maxLength={1000}
                style={{
                  padding: 10,
                  width: "100%",
                  height: 160,
                  border: "none",
                  pointerEvents: "all",
                }}
              />
            </div>

            <p>{`${values?.feedBackToVolunteer?.length}/1000`}</p>

            {errors?.feedBackToVolunteer && (
              <Alert variant="light">
                <p className="alert-description-text">
                  <InfoCircleFill
                    size={15}
                    color="error"
                    className="alert-description-icon"
                  />
                  {errors.feedBackToVolunteer}
                </p>
              </Alert>
            )}
          </FormControl.Group>

          {errors?.feedBackToVolunteer && (
            <Alert variant="light">
              <p className="alert-description-text">
                <InfoCircleFill
                  size={15}
                  fill="red"
                  className="alert-description-icon"
                />
                {errors.feedBackToVolunteer}
              </p>
            </Alert>
          )}

          {(role === UserRole.SENIOR || role === UserRole.CONTROLCENTER) && (
            <FormControl.Label className="text-area-report-details full-width-label">
              {t("commentToRegularController")}
              <FormControl.Control
                name="commentToRegularController"
                as="textarea"
                rows={4}
                className="extra-lower-padding fixed-height"
                value={values?.commentToRegularController || ""}
                onChange={handleChange}
              />
              {errors && errors?.commentToRegularController && (
                <Alert variant="light">
                  <p className="alert-description-text">
                    <InfoCircleFill
                      size={15}
                      fill="red"
                      className="alert-description-icon"
                    />
                    {errors.commentToRegularController}
                  </p>
                </Alert>
              )}
            </FormControl.Label>
          )}
          {role === UserRole.REGULAR && values?.commentToRegularController && (
            <FormControl.Label className="text-area-report-details full-width-label readOnlyMode">
              {t("commentToRegularController")}
              <FormControl.Control
                name="commentToRegularController"
                as="textarea"
                rows={4}
                className="extra-lower-padding fixed-height"
                value={values?.commentToRegularController || ""}
                onChange={handleChange}
              />
              {errors && errors?.commentToRegularController && (
                <Alert variant="light">
                  <p className="alert-description-text">
                    <InfoCircleFill
                      size={15}
                      fill="red"
                      className="alert-description-icon"
                    />
                    {errors.commentToRegularController}
                  </p>
                </Alert>
              )}
            </FormControl.Label>
          )}
        </InputGroup>
      </Stack>
    </div>
  );
};

export default FormFeedbackSection;
